import authHeader from "./auth-header";
import { callLogOut } from "../services/auth.service";
import { logger } from "../utils/logger";

const axios = require('axios');
const API_URL = process.env.REACT_APP_URI;
const axiosApiInstance = axios.create({
    baseURL: API_URL,
    // timeout: 10000,
});
// 요청별 retryCount를 관리하기 위한 Map
const retryCountMap = new Map();
const MAX_RETRY_COUNT = 3;
let isRefreshing = false;

let refreshSubscribers: Array<(token: string) => void> = [];

// 새로운 액세스 토큰을 받은 후 대기 중인 요청들을 처리
const onAccessTokenRefreshed = (newToken: string) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

axiosApiInstance.interceptors.response.use(
  (response: any) => response, // 성공 응답은 그대로 반환
  async (error: { config: any; response: { status: number; }; }) => {
    const originalRequest = error.config;

    if (!originalRequest) return Promise.reject(error);

    const requestKey = `${originalRequest.url}`;
    if (!retryCountMap.has(requestKey)) {
      retryCountMap.set(requestKey, 0); // 요청별 초기화
    }

    let retryCount = retryCountMap.get(requestKey) || 0;

    // 🔹 403 에러: 토큰 만료 시 재발급 처리
    if (error.response && error.response.status === 401) {
      if (retryCount >= MAX_RETRY_COUNT) {
        logger.error("401 error: Maximum retry limit reached.");
        callLogOut(); // 로그아웃 처리
        return Promise.reject(error);
      }

      retryCountMap.set(requestKey, retryCount + 1);
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            // ✅ 새로운 토큰으로 기본 헤더 업데이트
            axiosApiInstance.defaults.headers["Authorization"] = `Bearer ${newToken}`;
            
            // ✅ 원본 요청의 헤더도 업데이트
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            
            // ✅ 대기 중인 요청들에 새 토큰 전달
            onAccessTokenRefreshed(newToken);
            isRefreshing = false;
            
            logger.log("✅ Token refreshed, retrying original request");
            return axiosApiInstance(originalRequest); // ✅ 토큰 갱신 후 재요청
          } else {
            logger.error("❌ Token refresh failed: No new token received");
            callLogOut();
            isRefreshing = false;
            return Promise.reject(new Error("Token refresh failed"));
          }
        } catch (refreshError) {
          logger.error("❌ 401 error: Token refresh failed. Logging out...", refreshError);
          callLogOut();
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
      }

      // 기존 요청을 큐에 추가 후 새로운 토큰을 기다린 후 다시 요청
      return new Promise((resolve) => {
        refreshSubscribers.push((token: string) => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          resolve(axiosApiInstance(originalRequest));
        });
      });
    }

    return Promise.reject(error); // 다른 에러는 그대로 반환
  }
);

export const Axios = async (option: { method: string; url: string; data?: any; timeout?: any} ) => {
    let options = {
        headers: {
            'Access-Control-Allow-Origin' : '*',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
            ...authHeader()
        }
    }
    let request = {...options, ...option}
    // alert(JSON.stringify(request))
    try {
        const response = await axiosApiInstance(request);
        return response;
    } catch (error) {
        // console.log(error);
        return error;
    }
}

export const AxiosForm = async (option: { method: string; url: string; data?: any; } ) => {

    let optionsform = {
        headers: {
            'Access-Control-Allow-Origin' : '*',
            "Content-Type": "multipart/form-data" ,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
            ...authHeader(),
        }
    }
    let request = {...optionsform,  ...option}
    try {
        const response = await axiosApiInstance(request);
        return response;
    } catch (error) {
        // console.log(error);
        return error;
    }
}

const refreshAccessToken = async () => {
    const tokenStr = localStorage.getItem("s_u_t");
    let token: {
        refreshToken: any;
    } | null = null;
    if (tokenStr)
        token = JSON.parse(tokenStr);

    if (!token || !token.refreshToken) {
        logger.error("❌ refreshAccessToken: No refresh token found");
        callLogOut();
        return null;
    }
    
    try { 
        logger.log("🔄 Refreshing access token...");
        const response = await axios.post(`${API_URL}/auth/refresh-token`, {
            refresh_token: token.refreshToken,
        });
      
        logger.log("🔄 Refresh token response:", response.data);
        
        // 서버 응답에서 accessToken 필드 확인
        if (response.data.accessToken) {
            const newTokenData = {
                accessToken: response.data.accessToken,
                refreshToken: response.data.refreshToken || token.refreshToken, // 새 refreshToken이 있으면 사용, 없으면 기존 것 유지
            };
            
            localStorage.setItem("s_u_t", JSON.stringify(newTokenData));
            logger.log("✅ Access token refreshed successfully");
            return response.data.accessToken; // ✅ accessToken 반환
        } else {
            logger.error("❌ refreshAccessToken: No accessToken in response");
            logger.error("Response data:", response.data);
            callLogOut();
            return null;
        }
    } catch (error) {
        logger.error("❌ Error refreshing token:", error);
        callLogOut();
        return null;
    }
}

